import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Utility for combining class names with tailwind-merge
 */
export function cn(...inputs) {
    return twMerge(clsx(inputs))
}

/**
 * Format date helper
 */
export function formatDate(date, options = {}) {
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }

    return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(
        new Date(date)
    )
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date) {
    const now = new Date()
    const target = new Date(date)
    const diffInSeconds = Math.floor((now - target) / 1000)

    const intervals = [
        { label: 'year', seconds: 31536000 },
        { label: 'month', seconds: 2592000 },
        { label: 'day', seconds: 86400 },
        { label: 'hour', seconds: 3600 },
        { label: 'minute', seconds: 60 },
    ]

    for (const interval of intervals) {
        const count = Math.floor(diffInSeconds / interval.seconds)
        if (count >= 1) {
            return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`
        }
    }

    return 'Just now'
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text, length = 50) {
    if (!text || text.length <= length) return text
    return text.slice(0, length) + '...'
}

/**
 * Generate initials from name
 */
export function getInitials(name) {
    if (!name) return '??'

    return name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('')
}

/**
 * Format number with commas
 */
export function formatNumber(num) {
    if (num == null) return '0'
    return new Intl.NumberFormat('en-US').format(num)
}

/**
 * Debounce function
 */
export function debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout)
            func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
    }
}

/**
 * Generate random ID
 */
export function generateId(prefix = '') {
    const id = Math.random().toString(36).substr(2, 9)
    return prefix ? `${prefix}_${id}` : id
}

/**
 * Check if object is empty
 */
export function isEmpty(obj) {
    return obj == null || Object.keys(obj).length === 0
}

/**
 * Sleep/delay utility
 */
export function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Extract workspace and project from URL search params
 */
export function getWorkspaceProjectFromParams(searchParams) {
    const workspace = searchParams.get('workspace') || null
    const project = searchParams.get('project')

    return { workspace, project }
}

/**
 * Build URL with preserved query params
 */
export function buildUrl(path, additionalParams = {}) {
    const url = new URL(path, window.location.origin)

    // Preserve existing workspace/project params
    const currentParams = new URLSearchParams(window.location.search)
    const workspace = currentParams.get('workspace')
    const project = currentParams.get('project')

    if (workspace) url.searchParams.set('workspace', workspace)
    if (project) url.searchParams.set('project', project)

    // Preserve shared project params
    const shared = currentParams.get('shared')
    const uniqueUrl = currentParams.get('uniqueUrl')
    if (shared) url.searchParams.set('shared', shared)
    if (uniqueUrl) url.searchParams.set('uniqueUrl', uniqueUrl)

    // Add additional params
    Object.entries(additionalParams).forEach(([key, value]) => {
        if (value != null) {
            url.searchParams.set(key, value)
        }
    })

    return url.pathname + url.search
}

/**
 * Build workspace-only URL (excludes project parameter)
 * Used for workspace-level pages like domains
 */
export function buildWorkspaceUrl(path, additionalParams = {}) {
    const url = new URL(path, window.location.origin)

    // Only preserve workspace param for workspace-level pages
    const currentParams = new URLSearchParams(window.location.search)
    const workspace = currentParams.get('workspace')

    if (workspace) url.searchParams.set('workspace', workspace)

    // Add additional params
    Object.entries(additionalParams).forEach(([key, value]) => {
        if (value != null) {
            url.searchParams.set(key, value)
        }
    })

    return url.pathname + url.search
}

/**
 * Copy text to clipboard. Resolves true only when the text was actually copied.
 */
export async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text)
        return true
    } catch (err) {
        // Fallback for browsers or embedded views that block the async Clipboard API. Inside an open
        // modal the textarea must live in the dialog, or its focus trap steals focus before the copy.
        const previousFocus = document.activeElement
        const host = previousFocus?.closest?.('[role="dialog"]') || document.body
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.setAttribute('readonly', '')
        textArea.style.position = 'fixed'
        textArea.style.top = '0'
        textArea.style.left = '0'
        textArea.style.opacity = '0'
        host.appendChild(textArea)
        textArea.select()
        try {
            return document.execCommand('copy')
        } catch (fallbackErr) {
            return false
        } finally {
            host.removeChild(textArea)
            previousFocus?.focus?.({ preventScroll: true })
        }
    }
}
